import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/AuthProvider';
import { getFirestore, getDoc, doc } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import app from '../../../Firebase';
import { getLocationData, convertStoreAddressToServiceAddress } from '../../../service/LocationService';
import LocationPicker from './LocationPicker';
import './AddStoreWorkflow.css';

// LocalStorage keys for caching
const LOCATION_CONFIG_CACHE_KEY = 'dineplatform_location_config_cache';
const LOCATION_CONFIG_TIMESTAMP_KEY = 'dineplatform_location_config_timestamp';

// Cache duration: 30 minutes in milliseconds
const CACHE_DURATION = 30 * 60 * 1000;

interface Store {
  storeId?: string;
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  timezone: string;
  currency: string;
  stripeTestConnected: boolean;
  stripeLiveConnected: boolean;
  stripeConnected: boolean;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
  location?: {
    latitude: number;
    longitude: number;
  };
}

interface OrganizationConfig {
  id: string;
  orgId: string;
  orgName: string;
  stripeAccount: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  currencySymbol: string;
  region: string;
  availableTimezones: string[];
}

interface AddStoreWorkflowProps {
  functions: Functions;
  onSubmit: (storeData: Omit<Store, 'appId' | 'orgId' | 'stripeAccount'>) => void;
  onCancel: () => void;
  appRegion: string;
  organizationId: string;
  appId: string;
  appName: string;
  appType: string;
}

type WorkflowStep = 'details' | 'location' | 'complete';

// Utility functions for cache management
let inMemoryCache: { data: Record<string, OrganizationConfig> | null; timestamp: number } = { data: null, timestamp: 0 };

const getCachedLocationConfig = (): Record<string, OrganizationConfig> | null => {
  try {
    if (inMemoryCache.data && (Date.now() - inMemoryCache.timestamp < CACHE_DURATION)) {
      console.log('Using in-memory cache for AddStoreWorkflow');
      return inMemoryCache.data;
    }

    const cachedData = localStorage.getItem(LOCATION_CONFIG_CACHE_KEY);
    const timestamp = localStorage.getItem(LOCATION_CONFIG_TIMESTAMP_KEY);

    if (!cachedData || !timestamp) {
      return null;
    }

    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();

    if (now - cacheTime > CACHE_DURATION) {
      localStorage.removeItem(LOCATION_CONFIG_CACHE_KEY);
      localStorage.removeItem(LOCATION_CONFIG_TIMESTAMP_KEY);
      inMemoryCache = { data: null, timestamp: 0 };
      return null;
    }

    const parsedData = JSON.parse(cachedData);
    inMemoryCache = { data: parsedData, timestamp: cacheTime };
    return parsedData;
  } catch (error) {
    console.error('Error reading location config cache:', error);
    localStorage.removeItem(LOCATION_CONFIG_CACHE_KEY);
    localStorage.removeItem(LOCATION_CONFIG_TIMESTAMP_KEY);
    inMemoryCache = { data: null, timestamp: 0 };
    return null;
  }
};

const setCachedLocationConfig = (data: Record<string, OrganizationConfig>): void => {
  try {
    const timestamp = Date.now();
    localStorage.setItem(LOCATION_CONFIG_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(LOCATION_CONFIG_TIMESTAMP_KEY, timestamp.toString());
    inMemoryCache = { data, timestamp };
  } catch (error) {
    console.error('Error caching location config:', error);
  }
};

const AddStoreWorkflow: React.FC<AddStoreWorkflowProps> = ({
  functions,
  onSubmit,
  onCancel,
  appRegion,
  organizationId,
  appId,
  appName,
  appType
}) => {
  const { currentUser } = useAuth();

  const [currentStep, setCurrentStep] = useState<WorkflowStep>('details');
  const [isAppAdmin, setIsAppAdmin] = useState<boolean>(false);
  const [checkingPermission, setCheckingPermission] = useState<boolean>(true);
  const [organizationConfig, setOrganizationConfig] = useState<OrganizationConfig | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [isLoadingOrganizationConfig, setIsLoadingOrganizationConfig] = useState(true);
  const [isLoadingConfigInProgress, setIsLoadingConfigInProgress] = useState(false);

  const [formData, setFormData] = useState<Store>({
    name: appType === 'single-store' ? appName : '',
    description: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    timezone: '',
    currency: '',
    storeId: '',
    stripeTestConnected: false,
    stripeLiveConnected: false,
    stripeConnected: false,
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [detectedLocation, setDetectedLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  // Load organization config
  useEffect(() => {
    const loadOrganizationConfig = async (forceRefresh: boolean = false) => {
      if (!organizationId || !functions) {
        setIsLoadingOrganizationConfig(false);
        return;
      }

      if (isLoadingConfigInProgress && !forceRefresh) {
        return;
      }

      setIsLoadingConfigInProgress(true);
      setIsLoadingOrganizationConfig(true);

      try {
        if (!forceRefresh) {
          const cachedData = getCachedLocationConfig();
          if (cachedData && cachedData[organizationId]) {
            const config = cachedData[organizationId];
            setOrganizationConfig(config);
            setFormData(prev => ({
              ...prev,
              currency: config.currencyCode,
              country: config.country,
              timezone: config.availableTimezones[0] || 'UTC'
            }));
            setIsLoadingOrganizationConfig(false);
            setIsLoadingConfigInProgress(false);
            return;
          }
        }

        const getLocationConfig = httpsCallable(functions, 'getLocationConfig');
        const result = await getLocationConfig({});
        const data = result.data as { success: boolean; data: Record<string, OrganizationConfig> };

        if (data.success && data.data[organizationId]) {
          setCachedLocationConfig(data.data);
          const config = data.data[organizationId];
          setOrganizationConfig(config);
          setFormData(prev => ({
            ...prev,
            currency: config.currencyCode,
            country: config.country,
            timezone: config.availableTimezones[0] || 'UTC'
          }));
        } else {
          console.error('Organization not found in config data');
        }
      } catch (error) {
        console.error('Error loading organization config:', error);
      } finally {
        setIsLoadingOrganizationConfig(false);
        setIsLoadingConfigInProgress(false);
      }
    };

    loadOrganizationConfig();
  }, [organizationId, functions]);

  // Check admin permission
  useEffect(() => {
    const checkAdminPermission = async () => {
      if (!currentUser?.email) {
        setIsAppAdmin(false);
        setCheckingPermission(false);
        return;
      }

      try {
        const db = getFirestore(app);
        const userDocRef = doc(db, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userPermissions = userData?.permissions || [];
          setIsAppAdmin(userPermissions.includes('APP_ADMIN'));
        }
      } catch (error) {
        console.error('Error checking admin permission:', error);
      } finally {
        setCheckingPermission(false);
      }
    };

    if (currentUser) {
      checkAdminPermission();
    } else {
      setCheckingPermission(false);
    }
  }, [currentUser]);

  const isStepValid = (step: WorkflowStep) => {
    switch (step) {
      case 'details':
        return (
          formData.name.trim() !== '' &&
          formData.address.trim() !== '' &&
          formData.city.trim() !== '' &&
          formData.state.trim() !== '' &&
          formData.zipCode.trim() !== ''
        );
      case 'location':
        return detectedLocation !== null;
      default:
        return true;
    }
  };

  const handleInputChange = (field: keyof Store, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleProceedToLocation = async () => {
    if (!isStepValid('details') || !organizationConfig) return;

    setIsLoadingLocation(true);
    setLocationError('');

    try {
      const address = convertStoreAddressToServiceAddress(formData);
      const locationData = await getLocationData(address, organizationConfig.countryCode);

      if (locationData && locationData.latitude && locationData.longitude) {
        setDetectedLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude
        });
        setCurrentStep('location');
      } else {
        setLocationError('Could not find the location for this address. Please check the address and try again.');
      }
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationError('Error looking up address location. Please try again.');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const handleLocationChange = (latitude: number, longitude: number) => {
    setDetectedLocation({ latitude, longitude });
  };

  const handleCreateStore = async () => {
    if (!organizationConfig || !detectedLocation) {
      setSubmitError('Missing required data for store creation');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const createStoreFunction = httpsCallable(functions, 'createStore');

      const storeDataForCreation = {
        ...formData,
        description: `${formData.name} store`,
        location: {
          latitude: detectedLocation.latitude,
          longitude: detectedLocation.longitude
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const result = await createStoreFunction({
        storeData: storeDataForCreation,
        appId
      });

      const response = result.data as {
        success: boolean;
        storeId?: string;
        message?: string;
        error?: string;
      };

      if (response.success) {
        console.log('✅ Store created successfully:', response);
        setCurrentStep('complete');
      } else {
        throw new Error(response.error || 'Failed to create store');
      }
    } catch (error) {
      console.error('Error creating store:', error);
      setSubmitError('Failed to create store. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 'details') {
      handleProceedToLocation();
    } else if (currentStep === 'location') {
      handleCreateStore();
    }
  };

  const handleBack = () => {
    if (currentStep === 'location') {
      setCurrentStep('details');
      setLocationError('');
    } else {
      onCancel();
    }
  };

  const getCurrentStepTitle = () => {
    switch (currentStep) {
      case 'details': return 'Store Details';
      case 'location': return 'Location Verification';
      case 'complete': return 'Store Created';
      default: return 'Store Setup';
    }
  };

  const getNextButtonText = () => {
    if (currentStep === 'details') {
      return isLoadingLocation ? (
        <>
          <div className="spinner" />
          Finding Location...
        </>
      ) : 'Continue to Location';
    } else if (currentStep === 'location') {
      return isSubmitting ? (
        <>
          <div className="spinner" />
          Creating Store...
        </>
      ) : 'Create Store';
    }
    return 'Next';
  };

  const isNextDisabled = () => {
    if (currentStep === 'details') return !isStepValid('details') || isLoadingLocation;
    if (currentStep === 'location') return !isStepValid('location') || isSubmitting;
    return false;
  };

  const getProgress = () => {
    switch (currentStep) {
      case 'details': return 50;
      case 'location': return 100;
      default: return 0;
    }
  };

  const renderDetailsForm = () => (
    <div className="step-content">
      <div className="step-header">
        <h2>Store Details</h2>
        <p>Enter your store information and address</p>
      </div>

      <div className="form-section">
        <div className="form-group">
          <label htmlFor="store-name">Store Name *</label>
          <input
            id="store-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter your store name"
            className="form-input"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label htmlFor="store-address">Street Address *</label>
          <input
            id="store-address"
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="123 Main Street"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="store-city">City *</label>
            <input
              id="store-city"
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="City"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="store-state">State/Province *</label>
            <input
              id="store-state"
              type="text"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="State or Province"
              className="form-input"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="store-zip">Zip/Postal Code *</label>
            <input
              id="store-zip"
              type="text"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              placeholder="12345"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="store-country">Country *</label>
            <input
              id="store-country"
              type="text"
              value={formData.country}
              readOnly
              className="form-input readonly"
            />
            <small>Based on your organization settings</small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="store-timezone">Timezone *</label>
            <select
              id="store-timezone"
              value={formData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="form-select"
            >
              {organizationConfig?.availableTimezones.map((timezone: string) => (
                <option key={timezone} value={timezone}>
                  {timezone}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="store-currency">Currency *</label>
            <input
              id="store-currency"
              type="text"
              value={formData.currency}
              readOnly
              className="form-input readonly"
            />
            <small>Based on your organization settings</small>
          </div>
        </div>

        {locationError && <div className="error-message">{locationError}</div>}
      </div>
    </div>
  );

  const renderLocationForm = () => {
    const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}, ${formData.country}`;

    return (
      <div className="step-content">
        <div className="step-header">
          <h2>Verify Store Location</h2>
          <p>Confirm your store's exact location on the map below</p>
        </div>

        <div className="form-section">
          {detectedLocation && (
            <LocationPicker
              initialLatitude={detectedLocation.latitude}
              initialLongitude={detectedLocation.longitude}
              onLocationChange={handleLocationChange}
              address={fullAddress}
              className="location-picker"
            />
          )}
          {submitError && <div className="error-message">{submitError}</div>}
        </div>
      </div>
    );
  };

  if (checkingPermission || isLoadingOrganizationConfig) {
    return (
      <div className="add-store-workflow">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading store setup...</p>
        </div>
      </div>
    );
  }

  if (!organizationConfig) {
    return (
      <div className="add-store-workflow">
        <div className="error-state">
          <h3>Configuration Error</h3>
          <p>Could not load organization configuration. Please try refreshing the page.</p>
          <button onClick={onCancel} className="nav-button secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-store-workflow">
      {/* Header */}
      <div className="workflow-header">
        <button className="back-button" onClick={handleBack}>
          ← Back to {currentStep === 'details' ? appName : 'Store Details'}
        </button>
        <div className="org-info">
          <span>{organizationConfig.orgName}</span>
          <span className="org-region">({organizationConfig.region})</span>
        </div>
      </div>

      {/* Progress */}
      <div className="progress-section">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${getProgress()}%` }} />
        </div>
        <div className="progress-text">
          Step {currentStep === 'details' ? '1' : '2'} of 2: {getCurrentStepTitle()}
        </div>
      </div>

      {/* Content */}
      <div className="workflow-content-store">
        {currentStep === 'details' && renderDetailsForm()}
        {currentStep === 'location' && renderLocationForm()}
        {currentStep === 'complete' && (
          <div className="step-content">
            <div className="step-header">
              <h2>Store Created Successfully!</h2>
              <p>Your store has been created.</p>
            </div>
            <div className="form-section">
              <div className="success-summary">
                <div className="summary-item">
                  <strong>Store Name:</strong> {formData.name}
                </div>
                <div className="summary-item">
                  <strong>Location:</strong> {formData.address}, {formData.city}, {formData.state} {formData.zipCode}
                </div>
                <div className="summary-item">
                  <strong>Coordinates:</strong> {detectedLocation?.latitude.toFixed(6)}, {detectedLocation?.longitude.toFixed(6)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="workflow-navigation">
        <button
          type="button"
          className="nav-button secondary"
          onClick={handleBack}
          disabled={isLoadingLocation || isSubmitting}
        >
          {currentStep === 'details' ? 'Cancel' : currentStep === 'complete' ? 'Close' : 'Back'}
        </button>

        {currentStep === 'complete' ? (
          <button type="button" className="nav-button primary" onClick={onCancel}>
            Done
          </button>
        ) : (
          <button
            type="button"
            className="nav-button primary"
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            {getNextButtonText()}
          </button>
        )}
      </div>
    </div>
  );
};

export default AddStoreWorkflow;